import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Readonly } from "shared/ui/readonly/Readonly"
import { Tag } from "shared/ui/tags/Tags"

export interface BuildingReferencePageGeneralInfoViewModel {
  className?: string
  planner?: React.ReactNode
  owner?: React.ReactNode
  street?: React.ReactNode
  city?: React.ReactNode
  country?: React.ReactNode
  contact?: React.ReactNode
  locationContactDetails?: React.ReactNode
  client?: React.ReactNode
  architect?: React.ReactNode
  architectContactDetails?: React.ReactNode
  clientContactDetails?: React.ReactNode
  plannerContactDetails?: React.ReactNode
  mapsURL?: React.ReactNode
  additionalInformation?: React.ReactNode
  tags?: Tag[]
  isLoading?: boolean
  copyToClipboard: () => void
}

export const BuildingReferencePageGeneralInfo = React.memo(
  ({
    locationContactDetails,
    architectContactDetails,
    clientContactDetails,
    plannerContactDetails,
    additionalInformation,
    className,
    isLoading,
    copyToClipboard
  }: BuildingReferencePageGeneralInfoViewModel) => {
    const { t } = useTranslation()

    return (
      <div className={classNames("building-ref-general-info", className)}>
        <div className="building-ref-general-info__main">
          <div className="building-ref-general-info__section-header">
            <h2 className="building-ref-general-info__title">
              {t("BuildingReference.GeneralInformation")}
            </h2>
            <Button
              variant={ButtonVariant.SECONDARY}
              icon={IconName.DUPLICATE}
              iconSize={IconSize.TEXT}
              className="building-ref-general-info__copy-btn btn--borderless tablet-hidden"
              onClick={copyToClipboard}
            >
              {t("General.CopyToClipboard")}
            </Button>
          </div>

          {!isLoading ? (
            <Readonly.Root
              isLoading={isLoading}
              className="building-ref-general-info__location hide-empty-value"
            >
              <Readonly.Label>{t("BuildingReference.Location")}</Readonly.Label>
              <Readonly.Value>{locationContactDetails}</Readonly.Value>
            </Readonly.Root>
          ) : null}

          {isLoading || additionalInformation ? (
            <Readonly.Root
              isLoading={isLoading}
              className="building-ref-general-info__additional-info hide-empty-value"
            >
              <Readonly.Label>
                {t("BuildingReference.BuildingInformation")}
              </Readonly.Label>
              <Readonly.Value>{additionalInformation}</Readonly.Value>
            </Readonly.Root>
          ) : null}

          {isLoading ||
          architectContactDetails ||
          clientContactDetails ||
          plannerContactDetails ? (
            <div className="building-ref-general-info__contacts">
              {isLoading || clientContactDetails ? (
                <Readonly.Root
                  isLoading={isLoading}
                  className="building-ref-general-info__client-contact hide-empty-value"
                >
                  <Readonly.Label>
                    {t("BuildingReference.Client")}
                  </Readonly.Label>
                  <Readonly.Value>{clientContactDetails}</Readonly.Value>
                </Readonly.Root>
              ) : null}

              {isLoading || architectContactDetails ? (
                <Readonly.Root
                  isLoading={isLoading}
                  className="building-ref-general-info__architect-contact hide-empty-value"
                >
                  <Readonly.Label>
                    {t("BuildingReference.Architect")}
                  </Readonly.Label>
                  <Readonly.Value>{architectContactDetails}</Readonly.Value>
                </Readonly.Root>
              ) : null}

              {isLoading || plannerContactDetails ? (
                <Readonly.Root
                  isLoading={isLoading}
                  className="building-ref-general-info__planner-contact hide-empty-value"
                >
                  <Readonly.Label>
                    {t("BuildingReference.Planner")}
                  </Readonly.Label>
                  <Readonly.Value>{plannerContactDetails}</Readonly.Value>
                </Readonly.Root>
              ) : null}
            </div>
          ) : null}
          <Button
            variant={ButtonVariant.SECONDARY}
            icon={IconName.DUPLICATE}
            iconSize={IconSize.TEXT}
            className="building-ref-general-info__copy-btn btn--borderless desktop-hidden"
            onClick={copyToClipboard}
          >
            {t("General.CopyToClipboard")}
          </Button>
        </div>
      </div>
    )
  }
)
